import {Component, EventEmitter, OnDestroy, OnInit, Optional, Output, ViewChild} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {Subject, Subscription} from 'rxjs';

import {
    SiteApiService,
    LandApiService,
    LocationInfo,
    MapLocation,
    MapViewerComponent,
    AcabimSiteUpdateRequest, getErrorMessage
} from '@cas-hub/acabim-common-client';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MessageService, PrimeNGConfig, SelectItem} from 'primeng/api';

/// This component can be used to create or edit an existing site-and-project. If a AcabimSite is passed in, the component will behave in editing mode
/// otherwise it will behave in creating mode.

@Component({
    selector: 'acabim-admin-add-site',
    templateUrl: './add-site.component.html',
    styleUrls: ['./add-site.component.scss'],
    providers: [MessageService]

})
export class AddSiteComponent implements OnDestroy, OnInit {
    isLoading = false;
    public siteName = '';
    public selectedMapLocation?: MapLocation;
    public mapCoordinates?: number[][];
    siteForm: FormGroup | null = null;
    groupForm: FormGroup | undefined;
    groups: SelectItem[] = [];
    @Output() completed: EventEmitter<boolean> = new EventEmitter<boolean>();

    private _mapView?: MapViewerComponent;

    public get mapView(): MapViewerComponent | undefined {
        return this._mapView;
    }

    @ViewChild('mapViewer', {static: false})
    public set mapView(content: MapViewerComponent | undefined) {
        console.dir(content);
        this._mapView = content;
        if (content) {
            content.activateSearchMode().then();
        }
    }

    public get isProcessing(): boolean {
        return this._currentProcess !== undefined;
    }


    private _currentProcess?: Subscription = undefined;
    private readonly ngUnsubscribe = new Subject<void>();

    constructor(private readonly logger: NGXLogger,
                private readonly siteApiService: SiteApiService,
                private readonly landApiService: LandApiService,
                private fb: FormBuilder,
                private messageService: MessageService, private primengConfig: PrimeNGConfig,
                @Optional() private readonly dialogRef: DynamicDialogRef,
                @Optional() private readonly dynamicDialogConfig?: DynamicDialogConfig) {
        this.initForm();
        if (this.dynamicDialogConfig) {
        }

    }

    /**
     * Called when the user has changed the Map Location
     */
    public onMapLocationChanged(location: MapLocation): void {
        this.logger.debug('[SiteComponent] Location Change Event received: ', location);
        this.siteForm?.get('selectedMapLocation')?.setValue(location);
        this.selectedMapLocation = location;
        this.mapCoordinates = undefined;

        this.landApiService.getPathCoordinates(new LocationInfo(location.latitude, location.longitude))
            .subscribe(value => {
                this.siteForm?.get('mapCoordinates')?.setValue(value);
                this.mapCoordinates = value;
            });
    }

    public onSubmit(): void {
        if (confirm('Check if your site is shown on the map before proceeding')) {
            if (this.isProcessing) {
                this.messageService.clear();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Add/Update Site already processing - cancelling request'
                });
                return;
            }
        }
        this.addSite();
    }

    onCancel() {
        this.logger.debug('[SiteComponent] Add Site Modal Canceled');
        this.clearForm();
        this._currentProcess?.unsubscribe();
        this._currentProcess = undefined;

        this.dialogRef?.close(this);
    }

    private addSite(): void {
        if (this.siteForm) {
            if (!this.selectedMapLocation) {
                this.messageService.clear();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No MapLocation Selected - A Site needs a location'
                });
                return;
            }

            this.logger.debug('[SiteComponent] Adding new site-and-project');
            const siteName = this.siteForm.get('siteName')?.value ?? null;
            let siteGroups: string[] = [];
            if (this.groupForm) {
                siteGroups = this.groupForm.get('selectedGroups')?.value;
            }
            if (siteName && siteName.length > 3) {
                const request: AcabimSiteUpdateRequest = {
                    name: siteName,
                    latitude: this.selectedMapLocation.latitude,
                    longitude: this.selectedMapLocation.longitude,
                    address: this.selectedMapLocation.address,
                    groups: siteGroups
                };
                this.isLoading = true;
                this._currentProcess = this.siteApiService.createSite(request).subscribe(newSite => {
                    this.isLoading = false;
                    this.logger.debug('[SiteComponent] New site-and-project created successfully: ', newSite.id);
                    this.messageService.clear();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'New site-and-project created successfully: ' + newSite.name
                    });
                    this.clearForm();
                    this._currentProcess?.unsubscribe();
                    this._currentProcess = undefined;

                    this.dialogRef?.close(newSite);
                }, (error: any) => {
                    this.isLoading = false;
                    this._currentProcess?.unsubscribe();
                    this._currentProcess = undefined;
                    this.messageService.clear();
                    this.messageService.add(getErrorMessage(error));
                });
            } else {
                this.messageService.clear();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please enter a meaningful site name'
                });
                return;
            }
        } else {
            this.messageService.clear();
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'The form is not ready!'
            });
            return;
        }
    }


    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private initForm(): void {
        this.siteForm = new FormGroup({
            siteName: new FormControl(null, [Validators.required]),
            selectedMapLocation: new FormControl(null, [Validators.required]),
            mapCoordinates: new FormControl(null, [Validators.required]),
        });
    }

    private clearForm(): void {
        this.siteForm?.get('siteName')?.setValue(null);
        this.siteForm?.get('selectedMapLocation')?.setValue(null);
        this.siteForm?.get('mapCoordinates')?.setValue(null);
        this.siteName = '';
        this.selectedMapLocation = undefined;
        this.mapCoordinates = undefined;
    }

    ngOnInit(): void {
        this.primengConfig.ripple = true;

        this.groupForm = this.fb.group({
            selectedGroups: ['', Validators.nullValidator],
        });
        this.siteApiService.getAllSiteGroups().subscribe(data => {
            console.log(data);
            if (data && data.data) {
                data.data.map(d => {
                    this.groups.push({label: d.name, value: d.id});
                });

            }
        });
    }
}
